import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import CommonSelect from "../../../../core/common/commonSelect";
import ImageSelectDropMenu from "../../imageSelectDropMenu";
import { notify } from "../../../../services/toast.config";
import { getFullName } from "../../../ers/reports-settings/report-type/helpers/form.handler";

const ASSIGNED_TO_TYPE_OPTIONS = [
  { value: "user", label: "Employee" },
  { value: "location", label: "Location" },
  { value: "department", label: "Department" },
  { value: "not_assigned", label: "Select Assignment Type" },
];

interface EditProductModalProps {
  assetCategoryOptions: any;
  assetDepartmentOptions: any;
  assetLocationOptions: any;
  employeeOptions: any;
  statusOptions: any;
  updateUI: any;
  selectedAssetEdit: any;
  setSelectedAssetEdit: any;
  formDataCompanyChoose: any;
}

const EditAssignedAssetModal: React.FC<EditProductModalProps> = ({
  assetCategoryOptions,
  assetDepartmentOptions,
  assetLocationOptions,
  employeeOptions,
  statusOptions,
  updateUI,
  selectedAssetEdit,
  setSelectedAssetEdit,
  formDataCompanyChoose,
}) => {
  const signature = localStorage.getItem("signature");

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [originalAssetData, setOriginalAssetData] = useState(selectedAssetEdit);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string[];
  }>({});

  // Memoized Options
  const departmentSelectOptions = useMemo(
    () =>
      assetDepartmentOptions.map((dept: any) => ({
        label: dept.Department,
        value: dept.key.toString(),
      })),
    [assetDepartmentOptions]
  );

  const locationSelectOptions = useMemo(
    () =>
      assetLocationOptions.map((location: any) => ({
        label: location.location_name,
        value: location.id.toString(),
        imageUrl: null,
      })),
    [assetLocationOptions]
  );

  const employeeSelectOptions = useMemo(
    () =>
      employeeOptions.map((user: any) => ({
        label: `${getFullName(
          user?.salutation,
          user?.fname,
          user?.lname
        )}  - [${user?.emp_id || "N/F"}]`,
        value: user?.id?.toString(),
        imageUrl: user?.profile_pic || null,
      })),
    [employeeOptions]
  );

  // Utility Functions
  const getCurrentAssignmentType = () =>
    selectedAssetEdit?.assigned_to_type || "user";

  const isAssetAssigned = () =>
    selectedAssetEdit?.assigned_to_type &&
    selectedAssetEdit?.assigned_to_type !== "not_assigned";

  const getCategoryDisplayName = () => {
    const category = assetCategoryOptions?.find(
      (cat: any) => cat.id == selectedAssetEdit?.asset_category_id
    );
    return category?.Category || "Unknown Category";
  };

  // Validation Functions
  const validateForm = useCallback((): boolean => {
    if (isResetMode) return true; // Skip validation in reset mode

    const errors: { [key: string]: string[] } = {};
    const invalid = new Set<string>();

    // Check assignment type is not "not_assigned" unless explicitly reset
    const assignmentType = selectedAssetEdit?.assigned_to_type;
    if (!assignmentType || assignmentType === "not_assigned") {
      errors["assignment_type"] = ["Please select an assignment type"];
      invalid.add("assignment_type");
    }

    // Check that assignment target is selected if assignment type is set
    if (assignmentType && assignmentType !== "not_assigned") {
      if (!selectedAssetEdit?.assigned_to) {
        const targetLabel =
          assignmentType === "user"
            ? "employee"
            : assignmentType === "location"
            ? "location"
            : "department";
        errors["assignment_target"] = [`Please select an ${targetLabel}`];
        invalid.add("assignment_target");
      }
    }

    // Status validation
    if (!selectedAssetEdit?.status) {
      errors["status"] = ["Status is required"];
      invalid.add("status");
    }

    setValidationErrors(errors);
    setInvalidFields(invalid);
    return Object.keys(errors).length === 0;
  }, [selectedAssetEdit, isResetMode]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // Form Handlers
  const handleFormChange = (key: string, value: any) => {
    setSelectedAssetEdit((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleAssignmentTypeChange = (selectedOption: any) => {
    clearFieldError("assignment_type");
    clearFieldError("assignment_target");

    setSelectedAssetEdit((prev: any) => ({
      ...prev,
      assigned_to_type: selectedOption?.value || "",
      assigned_to: null,
    }));
  };

  const handleAssignmentTargetChange = (selectedTarget: any) => {
    clearFieldError("assignment_target");

    setSelectedAssetEdit((prev: any) => ({
      ...prev,
      assigned_to: selectedTarget?.value || null,
    }));
  };

  const handleStatusChange = (selectedStatus: any) => {
    clearFieldError("status");

    setSelectedAssetEdit((prev: any) => ({
      ...prev,
      status: selectedStatus?.value || "",
    }));
  };

  // Modal Actions
  const handleModalClose = () => {
    setIsResetMode(false);
    setOriginalAssetData(null);
    setInvalidFields(new Set());
    setValidationErrors({});
  };

  const handleResetAssignment = () => {
    setOriginalAssetData({ ...selectedAssetEdit });
    setSelectedAssetEdit((prev: any) => ({
      ...prev,
      assigned_to: null,
      assigned_by: null,
      assigned_to_type: "not_assigned",
      assigned_date: null,
      unassigned_date: null,
      status: "Inactive",
      notes: prev?.notes || "",
    }));
    setIsResetMode(true);
    // Clear all validation errors in reset mode
    setInvalidFields(new Set());
    setValidationErrors({});
  };

  const handleUndoReset = () => {
    if (originalAssetData) {
      setSelectedAssetEdit(originalAssetData);
    }
    setIsResetMode(false);
    setInvalidFields(new Set());
    setValidationErrors({});
  };

  // Submit Handler
  const handleEditSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      notify.error("Please fix the validation errors before submitting.");
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);

      if (isResetMode) {
        const dataToSend = {
          signature,
          id: selectedAssetEdit?.id,
          product_id: parseInt(selectedAssetEdit?.product_id),
          status: 0,
          company_id: parseInt(formDataCompanyChoose?.company_id),
          company_type: formDataCompanyChoose?.company_type,
          asset_category_id: parseInt(selectedAssetEdit?.asset_category_id),
          assigned_to_type: "not_assigned",
          unassigned_date: selectedAssetEdit?.unassigned_date || null,
          notes: selectedAssetEdit?.notes || null,
        };

        const response = await api.post(
          "assetService/updateAssingedAsset",
          dataToSend
        );
        notify.success(
          response.data.message?.[0]?.msg || "Assignment reset successfully!"
        );
        setIsResetMode(false);
        setOriginalAssetData(null);

        // Close modal
        const dismissButton = document.querySelector(
          "#dismiss-edit-asset-modal-btn"
        ) as HTMLButtonElement;
        if (dismissButton) {
          dismissButton.click();
        }
      } else {
        const finalAssignedToType =
          selectedAssetEdit?.assigned_to_type || "user";
        const finalAssignedTo = selectedAssetEdit?.assigned_to || null;
        const actualAssignedToType = finalAssignedTo
          ? finalAssignedToType
          : "not_assigned";

        const dataToSend = {
          id: parseInt(selectedAssetEdit?.id),
          signature,
          company_id: formDataCompanyChoose?.company_id,
          company_type: formDataCompanyChoose?.company_type,
          asset_category_id: parseInt(selectedAssetEdit?.asset_category_id),
          assigned_to_type: actualAssignedToType,
          product_id: parseInt(selectedAssetEdit?.product_id),
          assigned_date: selectedAssetEdit?.assigned_date || null,
          status: selectedAssetEdit?.status?.toLowerCase() === "active" ? 1 : 0,
          notes: selectedAssetEdit?.notes || null,
          assigned_to: finalAssignedTo,
        };

        const response = await api.post(
          "assetService/updateAssingedAsset",
          dataToSend
        );
        notify.success(
          response.data.message?.[0]?.msg || "Asset updated successfully!"
        );

        // Close modal
        const dismissButton = document.querySelector(
          "#dismiss-edit-asset-modal-btn"
        ) as HTMLButtonElement;
        if (dismissButton) {
          dismissButton.click();
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        error?.data?.message ||
        "An error occurred.";
      notify.error(errorMessage);
    } finally {
      await updateUI();
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Render Functions
  const renderAssignmentTargetField = () => {
    const currentType = getCurrentAssignmentType();
    const assignedTo = selectedAssetEdit?.assigned_to?.toString();
    const hasError = invalidFields.has("assignment_target");

    const getDefaultValue = (options: any[]) => {
      if (!assignedTo) return undefined;
      const found = options.find((option) => option.value === assignedTo);
      return found ? { value: assignedTo, label: found.label } : undefined;
    };

    switch (currentType) {
      case "user":
        return (
          <div
            className={
              hasError ? "is-invalid border border-danger rounded" : ""
            }>
            <ImageSelectDropMenu
              key={`employee_${selectedAssetEdit?.id}`}
              options={employeeSelectOptions}
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Employee"
              defaultValue={getDefaultValue(employeeSelectOptions)}
              isDisabled={isSubmitting}
            />
          </div>
        );

      case "department":
        return (
          <div
            className={
              hasError ? "is-invalid border border-danger rounded" : ""
            }>
            <CommonSelect
              className="select"
              options={departmentSelectOptions}
              defaultValue={getDefaultValue(departmentSelectOptions)}
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Department"
              isDisabled={isResetMode || isSubmitting}
            />
          </div>
        );

      case "location":
        return (
          <div
            className={
              hasError ? "is-invalid border border-danger rounded" : ""
            }>
            <ImageSelectDropMenu
              key={`location_${selectedAssetEdit?.id}`}
              options={locationSelectOptions}
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Location"
              defaultValue={getDefaultValue(locationSelectOptions)}
              isDisabled={isSubmitting}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderResetButton = () => (
    <div className="col-md-12 mb-3">
      <div
        className={`alert ${
          isResetMode ? "alert-info" : "alert-danger"
        } d-flex align-items-center justify-content-between flex-wrap`}>
        <div className="flex-grow-1 me-2">
          <span>
            {isResetMode
              ? "Reset mode - Add notes, unassigned date and submit to confirm reset"
              : "Reset assignment details while preserving the asset"}
          </span>
        </div>
        <div className="flex-shrink-0">
          {!isResetMode ? (
            <button
              type="button"
              onClick={handleResetAssignment}
              className="btn btn-outline-danger btn-sm d-flex align-items-center text-nowrap"
              disabled={isSubmitting}>
              <i className="ti ti-refresh me-1"></i>
              <span>Reset</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleUndoReset}
              className="btn btn-outline-info btn-sm d-flex align-items-center text-nowrap"
              disabled={isSubmitting}>
              <i className="ti ti-arrow-back me-1"></i>
              <span>Undo Reset</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Effects
  useEffect(() => {
    setIsResetMode(false);
    setOriginalAssetData(null);
    setInvalidFields(new Set());
    setValidationErrors({});
  }, [selectedAssetEdit?.id]);

  return (
    <>
      {isLoading && <Loader />}
      <div className="modal fade" id="edit_asset_modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Asset</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleEditSaveChanges}>
              {selectedAssetEdit && (
                <div className="modal-body pb-0">
                  <div className="row">
                    <div className="col-md-5 mb-3">
                      <label className="form-label">
                        Company <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={[]}
                        isDisabled={true}
                        defaultValue={{
                          value: formDataCompanyChoose.company_id,
                          label: `${formDataCompanyChoose.company_name} - ${formDataCompanyChoose.company_type}`,
                        }}
                      />
                    </div>

                    <div className="col-md-7 mb-3">
                      <label className="form-label">Selected Category</label>
                      <CommonSelect
                        key={`category_${selectedAssetEdit?.id}`}
                        className="select"
                        options={[]}
                        defaultValue={{
                          label:
                            selectedAssetEdit?.ProductDetails?.category
                              ?.breadcrumb || getCategoryDisplayName(),
                          value: selectedAssetEdit.asset_category_id,
                        }}
                        isDisabled={true}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Selected Product <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        key={`product_${selectedAssetEdit?.id}`}
                        className="select"
                        options={[]}
                        defaultValue={{
                          label: `${selectedAssetEdit?.product_name} [${selectedAssetEdit?.serial_no}]`,
                          value: selectedAssetEdit.product_id,
                        }}
                        isDisabled={true}
                      />
                    </div>

                    {renderResetButton()}

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Assignment Type <span className="text-danger">*</span>
                      </label>
                      <div
                        className={
                          invalidFields.has("assignment_type")
                            ? "is-invalid border border-danger rounded"
                            : ""
                        }>
                        <CommonSelect
                          className="select"
                          options={ASSIGNED_TO_TYPE_OPTIONS}
                          defaultValue={{
                            value: getCurrentAssignmentType(),
                            label:
                              ASSIGNED_TO_TYPE_OPTIONS.find(
                                (opt) =>
                                  opt.value === getCurrentAssignmentType()
                              )?.label || getCurrentAssignmentType(),
                          }}
                          onChange={handleAssignmentTypeChange}
                          placeHolder="Select Assignment Type"
                          isDisabled={isResetMode || isSubmitting}
                        />
                      </div>
                      {validationErrors["assignment_type"] && (
                        <div className="text-danger mt-1">
                          <small>
                            {validationErrors["assignment_type"][0]}
                          </small>
                        </div>
                      )}
                      <small className="text-info">
                        <i className="ti ti-info-circle me-1"></i>
                        {!isAssetAssigned()
                          ? "This asset is currently not assigned to anyone"
                          : "You can change the ownership of the asset!"}
                      </small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Select{" "}
                        {getCurrentAssignmentType() === "user"
                          ? "Employee"
                          : getCurrentAssignmentType() === "location"
                          ? "Location"
                          : getCurrentAssignmentType() === "department"
                          ? "Department"
                          : "Assignment Type"}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      {renderAssignmentTargetField()}
                      {validationErrors["assignment_target"] && (
                        <div className="text-danger mt-1">
                          <small>
                            {validationErrors["assignment_target"][0]}
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <div
                        className={
                          invalidFields.has("status")
                            ? "is-invalid border border-danger rounded"
                            : ""
                        }>
                        <CommonSelect
                          className="select"
                          options={statusOptions}
                          defaultValue={{
                            value: selectedAssetEdit.status,
                            label:
                              statusOptions.find(
                                (opt: any) =>
                                  opt.value === selectedAssetEdit.status
                              )?.label || selectedAssetEdit.status,
                          }}
                          onChange={handleStatusChange}
                          placeHolder="Select Status"
                          isDisabled={isResetMode || isSubmitting}
                        />
                      </div>
                      {validationErrors["status"] && (
                        <div className="text-danger mt-1">
                          <small>{validationErrors["status"][0]}</small>
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Updation Date</label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          onChange={(date) =>
                            handleFormChange("assigned_date", date)
                          }
                          disabled={isResetMode || isSubmitting}
                          value={
                            selectedAssetEdit?.assigned_date
                              ? dayjs(selectedAssetEdit?.assigned_date)
                              : null
                          }
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>

                    {isResetMode && (
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Unassigned Date</label>
                        <div className="input-icon-end position-relative">
                          <DatePicker
                            className="form-control datetimepicker border-info alert-info"
                            onChange={(date) =>
                              handleFormChange("unassigned_date", date)
                            }
                            disabled={isSubmitting}
                            value={
                              selectedAssetEdit?.unassigned_date
                                ? dayjs(selectedAssetEdit?.unassigned_date)
                                : null
                            }
                          />
                          <span className="input-icon-addon">
                            <i className="ti ti-calendar text-gray-7" />
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Remarks <span className="text-muted">(optional)</span>
                      </label>
                      <textarea
                        className={`form-control ${
                          isResetMode ? "border-info alert-info" : ""
                        }`}
                        placeholder={
                          isResetMode
                            ? "Please add notes explaining why this assignment is being reset..."
                            : "Enter any remarks..."
                        }
                        rows={2}
                        value={selectedAssetEdit?.notes || ""}
                        onChange={(e) =>
                          handleFormChange("notes", e.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={handleModalClose}
                  disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn ${
                    isResetMode ? "btn-info" : "btn-primary"
                  } me-2`}
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {isResetMode ? "Resetting..." : "Updating..."}
                    </>
                  ) : isResetMode ? (
                    "Confirm Reset"
                  ) : (
                    "Update Asset"
                  )}
                </button>

                <button
                  id="dismiss-edit-asset-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditAssignedAssetModal;
